/* eslint-disable @typescript-eslint/no-explicit-any */
import { Card, CardContent } from "@/components/ui/library/card";
import SearchInput from "@/components/ui/searchInput";
import Toggle from "@/components/common/inputs/Toggle";
import { Separator } from "@/components/ui/separator";
import FilterSection from "./components/FilterSection";
import CourseRow from "./components/CourseRow";
import {
  useInfiniteCourses,
} from "@/features/universityAndCourse/api/useCourses";
import { useUniversityOptions } from "@/features/universityAndCourse/api/useUniversities";
import {
  getUniversityCountries,
  reconcileUniversityForCountry,
  universitiesInCountry,
} from "@/features/universityAndCourse/utils/universityCountry";
import type { CourseFilterConfig } from "@/features/universityAndCourse/hooks/useCourseFilters";
import AddCourse from "@/features/universityAndCourse/components/add-edit-Course/addCourse";
import { usePermissions } from "@/rbac/usePermissions";
import { useAppDispatch } from "@/stores/hook";
import { setIsAddCourse } from "@/features/universityAndCourse/stores/courseFunctionalSlice";
import { useEducation } from "../../context/EducationContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useInfiniteScrollTrigger from "@/hooks/useInfiniteScrollTrigger";
import PageLoadingTrigger from "@/components/feedback/loading/PageLoadingTrigger";
import { Loader2, Maximize2, Plus } from "lucide-react";

const ALL = "all";

/** Sidebar route key of the "Universities & Courses" module (server TAB_BACKEND_REGISTRY). */
const UNIVERSITIES_ROUTE_KEY = "universities";

// Drawer filter row = Country + University ONLY (user, 2026-09-08: the Course
// Type / Match / Level selectors were removed from here). The full-page
// finder ("Open full view") keeps the complete facet set.
const ACTION_PILL_CLASS =
  "flex h-10 items-center gap-1.5 rounded-[10px] border border-[var(--color-green-4)] bg-gradient-to-b from-[#dcf5e7] to-[#c2f5da] px-3 text-[12px] font-medium tracking-[-0.2px] text-[var(--color-black-9)] shadow-[inset_0_1px_0_rgba(255,255,255,0.6),0_1px_2px_rgba(13,38,28,0.08)] transition-transform hover:-translate-y-[1px]";

export default function Course() {
  const { lead } = useEducation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { hasRouteKey } = usePermissions();
  // "Add course" here = the same sheet as the University & Course page, for
  // anyone whose role can open that module (route key), admins included
  // (hasRouteKey bypasses for them).
  const canAddCourse = hasRouteKey(UNIVERSITIES_ROUTE_KEY);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [eligibleOnly, setEligibleOnly] = useState(false);
  const [filters, setFilters] = useState<Record<string, string>>({
    country: ALL,
    university: ALL,
  });

  const { universities } = useUniversityOptions();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  const setFilter = (key: string, value: string) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      // COUNTRY → UNIVERSITY CASCADE: the university dropdown only ever lists
      // universities inside the picked country (see `universityOptions`), so
      // a pick that falls outside the new country must not linger as an
      // invisible course filter.
      if (key === "country") {
        next.university = reconcileUniversityForCountry(
          universities,
          value,
          prev.university,
          ALL,
        );
      }
      return next;
    });
  };

  const courseQueryFilters = useMemo(() => {
    const params: Record<string, string> = {};
    if (debouncedSearch) params.search = debouncedSearch;
    if (filters.country !== ALL) params.country = filters.country;
    if (filters.university !== ALL) params.universityId = filters.university;
    return params;
  }, [debouncedSearch, filters]);

  const {
    courses,
    totalCount,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteCourses(courseQueryFilters);

  const countryOptions = useMemo(() => {
    const values = Array.from<string>(
      new Set((universities ?? []).flatMap((university: any) => getUniversityCountries(university)))
    ).sort((a: string, b: string) => a.localeCompare(b));
    return values.map((value) => ({ value, label: value }));
  }, [universities]);

  // Universities inside the picked country only ("all" → every university).
  // The country is a custom-field value on the university, so this is the
  // client-side twin of the server's `country=<value>` university filter.
  const universityOptions = useMemo(
    () =>
      universitiesInCountry(universities ?? [], filters.country).map((u: any) => ({
        value: String(u.id),
        label: String(u.name ?? ""),
      })),
    [universities, filters.country],
  );

  const filterConfigs = useMemo<CourseFilterConfig[]>(
    () => [
      {
        key: "country",
        label: "Country",
        options: [{ value: ALL, label: "All Country" }, ...countryOptions],
      },
      {
        key: "university",
        label: "University",
        options: [{ value: ALL, label: "All Universities" }, ...universityOptions],
      },
    ],
    [countryOptions, universityOptions]
  );

  const filteredCourses = useMemo(() => {
    if (!eligibleOnly) return courses ?? [];
    return (courses ?? []).filter((course: any) => Boolean(course.matchPercentage));
  }, [courses, eligibleOnly]);

  const loadMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const { ref } = useInfiniteScrollTrigger(
    loadMore,
    Boolean(hasNextPage) && !isFetchingNextPage
  );

  const visibleCount = eligibleOnly ? filteredCourses.length : totalCount;

  const openFullFinder = () => {
    if (lead?.id) navigate(`/leads/${lead.id}/course-finder`);
  };

  return (
    <Card className="border-[var(--color-black-1)] rounded-[16px] shadow-none">
      <CardContent className="px-4">
        <div className="flex flex-col gap-4">
          {/* Pin below lead drawer tabs (ViewLead sets --view-lead-drawer-sticky-h). top-0 would cover the tab row. */}
          <div
            className="sticky z-10 flex flex-col gap-4 border-b border-[var(--color-black-1)] bg-white pb-3 pt-1 shadow-[0_1px_0_rgba(15,23,42,0.04)]"
            style={{ top: "var(--view-lead-drawer-sticky-h, 0px)" }}
          >
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <SearchInput
                  placeholder="Search by university or course name"
                  isKbd={false}
                  value={search}
                  onChange={(e: any) => setSearch(e.target.value)}
                />
              </div>
              {canAddCourse && (
                <AddCourse
                  trigger={
                    <button
                      type="button"
                      onClick={() => dispatch(setIsAddCourse(true))}
                      className={ACTION_PILL_CLASS}
                      title="Add a course to the University & Course module"
                    >
                      <Plus size={13} strokeWidth={2} />
                      Add course
                    </button>
                  }
                />
              )}
              <button
                type="button"
                onClick={openFullFinder}
                className={ACTION_PILL_CLASS}
                title="Open full course finder"
              >
                <Maximize2 size={13} strokeWidth={1.75} />
                Open full view
              </button>
            </div>

            <FilterSection
              configs={filterConfigs}
              filters={filters}
              onFilterChange={setFilter}
            />

            <Separator
              orientation="horizontal"
              className="!h-[1px] bg-[var(--color-black-2)]"
            />

            <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
              <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0 min-w-0">
                <span className="text-b2-mid text-[var(--color-black-10)]">
                  Courses
                </span>
                <span className="text-b2-mid text-[var(--color-text-secondary)] tabular-nums">
                  ({visibleCount})
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <p
                  className="text-b3-mid whitespace-nowrap"
                  style={{
                    background:
                      "linear-gradient(166.807deg, rgb(13, 38, 28) 59.642%, rgb(49, 155, 114) 107.05%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Eligible courses only
                </p>
                <Toggle checked={eligibleOnly} onChange={setEligibleOnly} />
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="size-5 animate-spin text-[var(--color-black-6)]" />
            </div>
          ) : filteredCourses.length === 0 ? (
            <div className="text-center py-10 text-[13px] text-[var(--color-text-gray)]">
              No courses match the current filters.
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {filteredCourses.map((course: any) => (
                <CourseRow key={course.id} course={course} />
              ))}
              <PageLoadingTrigger
                pageRef={ref}
                isFetchingNextPage={isFetchingNextPage}
                hasNextPage={Boolean(hasNextPage)}
                label="courses"
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
